import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { Button } from "../components/ui/button";
import { ShieldCheck, HandHeart, Coins, Users, ArrowRight, HeartHandshake, Wallet, Zap, Award, TrendingUp } from "lucide-react";
import TopInvitersLeaderboard from "../components/TopInvitersLeaderboard";

export default function Landing() {
  const { t, lang } = useLang();
  const { fmt, currency, rate } = useCurrency();

  return (
    <div className="min-h-screen relative overflow-hidden vibrant-bg">
      {/* Animated grid */}
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
        backgroundImage: "linear-gradient(rgba(0,229,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,255,0.1) 1px, transparent 1px)",
        backgroundSize: "60px 60px",
        maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)",
      }} />
      {/* Floating blobs */}
      <div className="absolute top-20 -end-32 w-96 h-96 rounded-full bg-[#00E5FF]/20 blur-3xl float-animation" />
      <div className="absolute bottom-20 -start-32 w-96 h-96 rounded-full bg-[#FF9500]/15 blur-3xl pulse-soft" />
      <div className="absolute top-1/2 start-1/3 w-80 h-80 rounded-full bg-[#00E676]/10 blur-3xl pulse-soft" style={{ animationDelay: "2s" }} />

      {/* Live ticker */}
      <div className="relative border-b border-[#1A2D4D] bg-[#0F1F35]/50 overflow-hidden">
        <div className="marquee-track py-2 text-xs text-[#8FA3BD] gap-12">
          {[...Array(2)].flatMap((_, k) => [
            `🤝 ${lang === "ar" ? "أكثر من 1,200 سلفة نشطة" : "1,200+ active pools"}`,
            `💵 1 USD = ${rate.toLocaleString()} د.ع`,
            `🌍 ${lang === "ar" ? "متاح في العراق وعالمياً" : "Available in Iraq & internationally"}`,
            `⚡ ${lang === "ar" ? "قرعة شفافة 100%" : "100% transparent draw"}`,
            `🎁 ${lang === "ar" ? "اربح من مشاهدة الإعلانات" : "Earn watching ads"}`,
          ].map((s, i) => <span key={`${k}-${i}`} className="flex items-center gap-3 whitespace-nowrap px-6">{s}<span className="text-[#1A2D4D]">•</span></span>))}
        </div>
      </div>

      <section className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-12 pb-24">
        {/* Prominent centered logo */}
        <div className="flex flex-col items-center mb-10 animate-fade-up">
          <div className="relative">
            <div className="absolute inset-0 bg-[#00E5FF]/30 blur-3xl rounded-full animate-pulse" />
            <img
              src="/assets/sulfa-logo.png"
              alt="SULFA"
              className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-3xl object-contain drop-shadow-[0_0_30px_rgba(0,229,255,0.5)]"
              data-testid="hero-logo"
            />
          </div>
          <div className="mt-4 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-gold-gradient">سلفة • SULFA</h2>
            <p className="text-xs text-[#8FA3BD] mt-1">{lang === "ar" ? "تكافل اجتماعي" : "Social Takaful"}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full badge-vibrant text-xs mb-6">
              <HeartHandshake className="w-3.5 h-3.5" />
              <span>تكافل اجتماعي • Social Takaful</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-cyan-gradient leading-tight mb-6">
              {t("tagline")}
            </h1>
            <p className="text-[#8FA3BD] text-lg max-w-xl mb-10 leading-relaxed">{t("subtitle")}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="btn-cyan text-base px-8 glow-pulse" data-testid="hero-get-started-btn">
                  {t("getStarted")} <ArrowRight className="w-4 h-4 ms-2 rtl:rotate-180" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-[#1A2D4D] text-white hover:bg-[#1A2D4D]" data-testid="hero-login-btn">
                  {t("login")}
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-3 gap-6 mt-14 max-w-md">
              {[
                { l: t("tierBronze"), v_min: 5, v_max: 20, c: "tier-bronze" },
                { l: t("tierSilver"), v_min: 50, v_max: 100, c: "tier-silver" },
                { l: t("tierGold"), v_min: 100, v_max: 500, c: "tier-gold" },
              ].map((s, i) => (
                <div key={i} className="animate-fade-up" style={{ animationDelay: `${0.2 + i * 0.1}s` }}>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.c}`}>{s.l}</span>
                  <div className="text-base sm:text-lg font-bold text-white mt-2" data-testid={`tier-range-${i}`}>
                    {fmt(s.v_min, { decimals: 0 })} - {fmt(s.v_max, { decimals: 0 })}
                  </div>
                </div>
              ))}
            </div>

            {/* Currency hint */}
            <div className="mt-6 inline-flex items-center gap-2 text-xs text-[#8FA3BD] bg-[#0F1F35]/80 px-3 py-1.5 rounded-full border border-[#1A2D4D]">
              <TrendingUp className="w-3 h-3 text-[#00E676]" />
              {lang === "ar"
                ? `العرض بـ ${currency === "IQD" ? "الدينار العراقي" : "الدولار"} — يمكن تغييره من الأعلى`
                : `Showing ${currency} — toggle from the top`}
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            <div className="bento-card p-6 col-span-2 glow-pulse animate-fade-up" style={{ animationDelay: "0.1s" }}>
              <HandHeart className="w-7 h-7 text-[#00E5FF] mb-3" />
              <div className="text-xl font-semibold">{t("trustTitle")}</div>
              <div className="text-sm text-[#8FA3BD] mt-1">
                {lang === "ar"
                  ? "مجموعة من الأعضاء يدعمون بعضهم — كل عضو يساهم بقدر متفق عليه ويستلم دوره عبر قرعة شفافة"
                  : "Members support each other — each contributes an agreed amount and receives their turn via a transparent draw"}
              </div>
            </div>
            <div className="bento-card p-6 animate-fade-up" style={{ animationDelay: "0.2s" }}>
              <Award className="w-6 h-6 text-[#FF9500] mb-3" />
              <div className="font-semibold text-sm">XP & Tiers</div>
              <div className="text-xs text-[#8FA3BD] mt-1">{t("unlockNext")}</div>
            </div>
            <div className="bento-card p-6 animate-fade-up" style={{ animationDelay: "0.3s" }}>
              <Coins className="w-6 h-6 text-[#00E5FF] mb-3" />
              <div className="font-semibold text-sm">{t("watchAd")}</div>
              <div className="text-xs text-[#8FA3BD] mt-1">{t("coinsRate")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values section */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 border-t border-[#1A2D4D]">
        <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-cyan-gradient">{lang === "ar" ? "قيمنا" : "Our Values"} • {lang === "ar" ? "Our Values" : "قيمنا"}</h2>
        <p className="text-sm text-[#8FA3BD] mb-10 max-w-2xl">
          {lang === "ar"
            ? "منصة تكافل ودعم متبادل — كل عضو يدعم بقية الأعضاء ويصل دوره ليحصل على دفعة مالية تساعده في احتياجاته."
            : "Mutual support platform — each member supports the others and receives a turn for a financial boost when needed."}
        </p>
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: HandHeart, t_ar: "تكافل اجتماعي", t_en: "Social Takaful", d_ar: "كل عضو يساهم لمساعدة باقي الأعضاء — نظام شبيه بالجمعيات التقليدية", d_en: "Each member contributes to help others — similar to traditional savings groups" },
            { icon: ShieldCheck, t_ar: "شفافية كاملة", t_en: "Full Transparency", d_ar: "توثيق هوية إجباري وقرعة عشوائية تشاهدها أمامك مباشرة", d_en: "Mandatory ID verification and random draw visible to everyone" },
            { icon: Users, t_ar: "مجتمع موثوق", t_en: "Trusted Community", d_ar: "منع تعدد الحسابات بصرامة، نظام مستويات يكافئ الالتزام", d_en: "Strict anti multi-account, tier system rewarding commitment" },
          ].map((v, i) => {
            const Icon = v.icon;
            return (
              <div key={i} className="bento-card p-6 animate-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <Icon className="w-8 h-8 text-[#00E5FF] mb-4" />
                <div className="text-lg font-semibold mb-2">{lang === "ar" ? v.t_ar : v.t_en}</div>
                <p className="text-sm text-[#8FA3BD] leading-relaxed">{lang === "ar" ? v.d_ar : v.d_en}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 border-t border-[#1A2D4D]">
        <h2 className="text-2xl sm:text-3xl font-bold mb-10">{t("howItWorks")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, txt: t("step1") },
            { icon: Wallet, txt: t("step2") },
            { icon: HeartHandshake, txt: t("step3") },
            { icon: Zap, txt: t("step4") },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bento-card p-6 relative animate-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="absolute top-4 end-4 text-[#1A2D4D] text-5xl font-bold">{i + 1}</div>
                <Icon className="w-7 h-7 text-[#00E5FF] mb-4" />
                <p className="text-sm text-[#F0F6FF]">{s.txt}</p>
              </div>
            );
          })}
        </div>
      </section>

      <TopInvitersLeaderboard />

      <footer className="border-t border-[#1A2D4D] py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-xs text-[#4A5970]">
          © 2026 SULFA — {t("trustDesc")}.
        </div>
      </footer>
    </div>
  );
}
